import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Group,
  Person,
  School,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import SupervisorDropdown from '../components/SupervisorDropdown';

interface Project {
  id: number;
  title: string;
  status: string;
  student_name?: string;
  supervisor_name?: string;
}

interface TeamMember {
  id: number;
  user_id: number;
  full_name: string;
  username: string;
  user_role: string;
  role: string;
  project_id: number;
  project_title: string;
}

interface User {
  id: number;
  full_name: string;
  username: string;
  role: string;
}

const Teams: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [selectedRole, setSelectedRole] = useState<string>('member');
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<number | ''>('');
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: allProjects = [] } = useQuery<Project[]>('projects', async () => {
    const response = await api.get('/projects');
    return response.data.projects;
  });

  // Filter projects by selected supervisor
  const projects = allProjects.filter(project => {
    if (!selectedSupervisorId) return true;
    // This would need to be updated based on your project structure
    // For now, we'll assume projects have a supervisor_id field
    return (project as any).supervisor_id === selectedSupervisorId;
  });

  const { data: teamMembers = [] } = useQuery<TeamMember[]>(
    ['team-members', selectedProject?.id],
    async () => {
      if (!selectedProject) return [];
      const response = await api.get(`/teams/project/${selectedProject.id}`);
      // Hide supervisors from the visible team list per requirement
      return (response.data.teamMembers as TeamMember[]).filter(m => m.user_role !== 'supervisor');
    },
    {
      enabled: !!selectedProject,
    }
  );

  const { data: availableUsers = [] } = useQuery<User[]>(
    ['available-users', selectedProject?.id],
    async () => {
      if (!selectedProject) return [];
      const response = await api.get(`/teams/available-users/${selectedProject.id}`);
      // Filter out supervisors from available users
      return response.data.users.filter((user: User) => user.role !== 'supervisor');
    },
    {
      enabled: !!selectedProject,
    }
  );

  const addTeamMemberMutation = useMutation(
    async (teamData: { project_id: number; user_id: number; role: string }) => {
      const response = await api.post('/teams', teamData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['team-members', selectedProject?.id]);
        queryClient.invalidateQueries(['available-users', selectedProject?.id]);
        setOpenDialog(false);
        setSelectedUser('');
        setSelectedRole('member');
        enqueueSnackbar('User added to team successfully!', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to add user to team', { variant: 'error' });
      },
    }
  );

  const updateTeamMemberMutation = useMutation(
    async ({ id, role }: { id: number; role: string }) => {
      const response = await api.put(`/teams/${id}`, { role });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['team-members', selectedProject?.id]);
        setOpenDialog(false);
        setEditingMember(null);
        enqueueSnackbar('Team member updated successfully!', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to update team member', { variant: 'error' });
      },
    }
  );

  const removeTeamMemberMutation = useMutation(
    async (id: number) => {
      await api.delete(`/teams/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['team-members', selectedProject?.id]);
        queryClient.invalidateQueries(['available-users', selectedProject?.id]);
        enqueueSnackbar('User removed from team successfully!', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to remove user from team', { variant: 'error' });
      },
    }
  );

  const handleAddMember = () => {
    if (selectedUser && selectedProject) {
      addTeamMemberMutation.mutate({
        project_id: selectedProject.id,
        user_id: selectedUser as number,
        role: selectedRole,
      });
    }
  };

  const handleUpdateMember = () => {
    if (editingMember) {
      updateTeamMemberMutation.mutate({
        id: editingMember.id,
        role: selectedRole,
      });
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <AdminPanelSettings />;
      case 'supervisor':
        return <School />;
      case 'student':
        return <Person />;
      default:
        return <Person />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'supervisor':
        return 'warning';
      case 'student':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTeamRoleColor = (role: string) => {
    switch (role) {
      case 'lead':
        return 'error';
      case 'developer':
        return 'primary';
      case 'tester':
        return 'warning';
      case 'documentation':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Project Teams</Typography>
        <Box sx={{ minWidth: 200 }}>
          <SupervisorDropdown
            value={selectedSupervisorId}
            onChange={setSelectedSupervisorId}
            label="Filter by Supervisor"
            showAvatar={true}
            showRole={false}
            size="small"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Project Selection */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Select Project
            </Typography>
            <List>
              {projects.map((project) => (
                <ListItem
                  key={project.id}
                  button
                  selected={selectedProject?.id === project.id}
                  onClick={() => setSelectedProject(project)}
                >
                  <ListItemText
                    primary={project.title}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Status: {project.status}
                        </Typography>
                        {project.student_name && (
                          <Typography variant="body2" color="text.secondary">
                            Student: {project.student_name}
                          </Typography>
                        )}
                        {project.supervisor_name && (
                          <Typography variant="body2" color="text.secondary">
                            Supervisor: {project.supervisor_name}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Team Members */}
        <Grid item xs={12} md={8}>
          {selectedProject ? (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Team Members - {selectedProject.title}
                </Typography>
                {user?.role !== 'student' && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setOpenDialog(true)}
                  >
                    Add Member
                  </Button>
                )}
              </Box>

              <Grid container spacing={2}>
                {teamMembers.map((member) => (
                  <Grid item xs={12} sm={6} md={4} key={member.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Avatar sx={{ mr: 1 }}>
                            {getRoleIcon(member.user_role)}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2">
                              {member.full_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              @{member.username}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Chip
                            label={member.user_role}
                            color={getRoleColor(member.user_role) as any}
                            size="small"
                          />
                          <Chip
                            label={member.role}
                            color={getTeamRoleColor(member.role) as any}
                            size="small"
                          />
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => {
                            setEditingMember(member);
                            setSelectedRole(member.role);
                            setOpenDialog(true);
                          }}
                        >
                          Edit Role
                        </Button>
                        {user?.role !== 'student' && (
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => removeTeamMemberMutation.mutate(member.id)}
                          >
                            Remove
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {teamMembers.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No team members assigned to this project
                </Typography>
              )}
            </Paper>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Group sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Select a project to manage its team
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Add/Edit Member Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMember ? 'Edit Team Member Role' : 'Add Team Member'}
        </DialogTitle>
        <DialogContent>
          {!editingMember && (
            <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUser}
                label="Select User"
                onChange={(e) => setSelectedUser(Number(e.target.value))}
              >
                {availableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        {getRoleIcon(user.role)}
                      </Avatar>
                      {user.full_name} ({user.role})
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth>
            <InputLabel>Team Role</InputLabel>
            <Select
              value={selectedRole}
              label="Team Role"
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="lead">Lead</MenuItem>
              <MenuItem value="developer">Developer</MenuItem>
              <MenuItem value="tester">Tester</MenuItem>
              <MenuItem value="documentation">Documentation</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editingMember ? handleUpdateMember : handleAddMember}
            disabled={!editingMember && !selectedUser}
          >
            {editingMember ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Teams; 